
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Droplet, Sun, Leaf, Flower, Check, Timer, ShieldAlert, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Product } from "@/components/shop/ShopTypes";
import ProductCard from "@/components/shop/ProductCard";
import { findMatchingProducts } from "@/components/deodorant-quiz/quizLogic";

// Define quiz questions structure
interface QuizQuestion {
  id: number;
  question: string;
  icon: React.ReactNode;
  options: {
    id: string;
    text: string;
    value: string;
  }[];
  type: "single" | "multiple";
  explanation: string;
}

// Define the quiz questions
const questions: QuizQuestion[] = [
  {
    id: 1,
    question: "What's your skin type?",
    icon: <Droplet className="h-6 w-6 text-primary" />,
    type: "single",
    options: [
      { id: "1a", text: "Normal", value: "normal" },
      { id: "1b", text: "Sensitive", value: "sensitive" },
      { id: "1c", text: "Dry", value: "dry" },
      { id: "1d", text: "Oily", value: "oily" }
    ],
    explanation: "Helps filter deodorants with gentle or hydrating formulas."
  },
  {
    id: 2,
    question: "How much do you sweat daily?",
    icon: <Sun className="h-6 w-6 text-primary" />,
    type: "single",
    options: [
      { id: "2a", text: "Barely at all", value: "minimal" },
      { id: "2b", text: "A little", value: "light" },
      { id: "2c", text: "A lot", value: "heavy" },
      { id: "2d", text: "Extreme (e.g., during workouts or under stress)", value: "extreme" }
    ],
    explanation: "Matches strength of protection (regular, clinical, sport, etc.)"
  },
  {
    id: 3,
    question: "Do you prefer natural ingredients?",
    icon: <Leaf className="h-6 w-6 text-primary" />,
    type: "single",
    options: [
      { id: "3a", text: "Yes", value: "yes" },
      { id: "3b", text: "No", value: "no" },
      { id: "3c", text: "Doesn't matter", value: "neutral" }
    ],
    explanation: "Filters natural, aluminum-free options."
  },
  {
    id: 4,
    question: "What type of scent do you prefer?",
    icon: <Flower className="h-6 w-6 text-primary" />,
    type: "single",
    options: [
      { id: "4a", text: "Fresh/Clean", value: "fresh" },
      { id: "4b", text: "Floral", value: "floral" },
      { id: "4c", text: "Fruity", value: "fruity" },
      { id: "4d", text: "Earthy/Woody", value: "earthy" },
      { id: "4e", text: "Unscented", value: "unscented" }
    ],
    explanation: "Helps recommend scent families."
  },
  {
    id: 5,
    question: "Do you experience irritation from deodorants?",
    icon: <AlertTriangle className="h-6 w-6 text-primary" />,
    type: "single",
    options: [
      { id: "5a", text: "Yes, often", value: "often" },
      { id: "5b", text: "Sometimes", value: "sometimes" },
      { id: "5c", text: "Never", value: "never" }
    ],
    explanation: "Helps identify deodorants suitable for sensitive skin."
  },
  {
    id: 6,
    question: "Any specific concerns?",
    icon: <ShieldAlert className="h-6 w-6 text-primary" />,
    type: "multiple",
    options: [
      { id: "6a", text: "Stains on clothes", value: "stain_free" },
      { id: "6b", text: "Aluminum-free", value: "aluminum_free" },
      { id: "6c", text: "Vegan/cruelty-free", value: "vegan" },
      { id: "6d", text: "Long-lasting odor protection", value: "long_lasting" },
      { id: "6e", text: "None", value: "none" }
    ],
    explanation: "Prioritizes specific user concerns."
  }
];

const DeodorantQuiz = () => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [matchedProducts, setMatchedProducts] = useState<Product[]>([]);
  
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  
  const handleSingleOptionSelect = (optionId: string) => {
    const selectedOption = currentQuestion.options.find(option => option.id === optionId);
    if (selectedOption) {
      setAnswers({
        ...answers,
        [currentQuestion.id]: selectedOption.value
      });
    }
  };
  
  const handleMultipleOptionSelect = (optionId: string) => {
    const currentSelections = answers[currentQuestion.id] as string[] || [];
    const selectedOption = currentQuestion.options.find(option => option.id === optionId);
    
    if (!selectedOption) return;
    
    // If "None" is selected, clear other selections
    if (selectedOption.value === "none") {
      setAnswers({
        ...answers,
        [currentQuestion.id]: ["none"]
      });
      return;
    }
    
    // If other option is selected, remove "None" if present
    let newSelections = currentSelections.includes(selectedOption.value)
      ? currentSelections.filter(value => value !== selectedOption.value)
      : [...currentSelections.filter(value => value !== "none"), selectedOption.value];
    
    setAnswers({
      ...answers,
      [currentQuestion.id]: newSelections
    });
  };
  
  const isOptionChecked = (optionId: string): boolean => {
    const answer = answers[currentQuestion.id];
    if (!answer) return false;
    
    if (Array.isArray(answer)) {
      const selectedOption = currentQuestion.options.find(option => option.id === optionId);
      return selectedOption ? answer.includes(selectedOption.value) : false;
    } else {
      const selectedOption = currentQuestion.options.find(option => option.id === optionId);
      return selectedOption ? answer === selectedOption.value : false;
    }
  };
  
  const nextQuestion = () => {
    // Ensure an answer is selected for single-choice questions
    if (currentQuestion.type === "single" && !answers[currentQuestion.id]) {
      toast({
        title: "Please select an answer",
        description: "You need to select an option before continuing.",
        variant: "destructive",
      });
      return;
    }
    
    // Ensure at least one answer is selected for multiple-choice questions
    if (currentQuestion.type === "multiple" && 
        (!answers[currentQuestion.id] || 
         (Array.isArray(answers[currentQuestion.id]) && 
          (answers[currentQuestion.id] as string[]).length === 0))) {
      toast({
        title: "Please select at least one option",
        description: "You need to select at least one option before continuing.",
        variant: "destructive",
      });
      return;
    }
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Find matching products based on answers
      const products = findMatchingProducts(answers);
      setMatchedProducts(products);
      setQuizCompleted(true);
    }
  };
  
  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  
  const restartQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setQuizCompleted(false);
    setMatchedProducts([]);
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 py-8 bg-gradient-to-b from-white to-violet-50">
        <div className="container-custom">
          <h1 className="text-3xl font-bold text-center mb-2">Find Your Perfect Deodorant</h1>
          <p className="text-center mb-8 text-muted-foreground">
            Answer these questions to discover the ideal deodorant for your needs
          </p>
          
          {!quizCompleted ? (
            <Card className="w-full max-w-3xl mx-auto p-6 shadow-md">
              <div className="mb-6">
                <Progress value={progress} className="h-2" />
                <p className="text-right text-sm text-muted-foreground mt-2">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </p>
              </div>
              
              <div className="flex items-center gap-3 mb-6">
                {currentQuestion.icon}
                <h2 className="text-xl font-semibold">{currentQuestion.question}</h2>
              </div>
              
              {currentQuestion.type === "single" ? (
                <RadioGroup
                  value={
                    answers[currentQuestion.id] && !Array.isArray(answers[currentQuestion.id])
                      ? currentQuestion.options.find(o => o.value === answers[currentQuestion.id])?.id
                      : undefined
                  }
                  onValueChange={handleSingleOptionSelect}
                  className="space-y-3 mb-6"
                >
                  {currentQuestion.options.map((option) => (
                    <div key={option.id} className="flex items-start space-x-2 p-3 rounded-md hover:bg-slate-50">
                      <RadioGroupItem value={option.id} id={option.id} />
                      <label 
                        htmlFor={option.id} 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                      >
                        {option.text}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              ) : (
                <div className="space-y-3 mb-6">
                  {currentQuestion.options.map((option) => (
                    <div 
                      key={option.id} 
                      className="flex items-start space-x-2 p-3 rounded-md hover:bg-slate-50"
                      onClick={() => handleMultipleOptionSelect(option.id)}
                    >
                      <Checkbox
                        id={option.id}
                        checked={isOptionChecked(option.id)}
                        onCheckedChange={() => handleMultipleOptionSelect(option.id)}
                      />
                      <label 
                        htmlFor={option.id} 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                      >
                        {option.text}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-2 p-4 bg-violet-50 rounded-md">
                <p className="text-sm text-muted-foreground">{currentQuestion.explanation}</p>
              </div>
              
              <div className="mt-6 flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={prevQuestion} 
                  disabled={currentQuestionIndex === 0}
                >
                  Previous
                </Button>
                <Button onClick={nextQuestion}>
                  {currentQuestionIndex < questions.length - 1 ? "Next" : "See Results"}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="w-full max-w-5xl mx-auto">
              <Card className="p-6 mb-8 text-center shadow-md">
                <h2 className="text-2xl font-bold mb-4">Your Ideal Deodorant Is...</h2>
                <p className="mb-4 text-muted-foreground">
                  Based on your preferences, we've found these products that match your needs:
                </p>
              </Card>
              
              <div className="space-y-8">
                {matchedProducts.length > 0 ? (
                  matchedProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))
                ) : (
                  <div className="text-center p-8">
                    <p className="text-lg mb-4">No exact matches found. Here are some general recommendations:</p>
                    <div className="space-y-8">
                      <ProductCard product={{
                        id: 1,
                        name: "Kinko Care Natural Deodorant",
                        description: "100% natural and vegan deodorant that effectively neutralizes odor. Free from aluminum, alcohol, and baking soda.",
                        price: "$18.00",
                        image: "/lovable-uploads/a2ef7eb0-0c25-4d2e-885a-ecbd6e258b4a.png",
                        link: "https://www.kinko.care/?ref=rlexraps"
                      }} />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={restartQuiz} variant="outline">
                  Take Quiz Again
                </Button>
                <Button onClick={() => navigate("/shop")}>
                  Browse All Products
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default DeodorantQuiz;
